import { VideoCameraAddOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, type ToOptions } from '@tanstack/react-router';
import { App, Button, Dropdown, type ButtonProps } from 'antd';
import { useSetFrameImagePageData } from '../../frame-image/store';
import { useSetReferenceImagePageData } from '../../reference-image/store';
import { createScriptMutation } from '../../script/api';
import { ScriptType } from '../../script/model';
import type { ScriptDto } from '../../script/script.dto';

export const NavigateButton = ({
  script,
  enableAutoSave = false,
  ...props
}: ButtonProps & {
  script: ScriptDto;
  enableAutoSave?: boolean;
}) => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const setReferenceImagePageData = useSetReferenceImagePageData(false);
  const setLongReferenceImagePageData = useSetReferenceImagePageData(true);
  const setFrameImagePageData = useSetFrameImagePageData();
  const createScript = useMutation(createScriptMutation);

  const handleCreateVideo = async (script: ScriptDto, to: ToOptions['to']) => {
    if (enableAutoSave) {
      await createScript.mutateAsync({
        type: ScriptType.Video,
        data: script,
        tags: null,
      });
    }

    switch (to) {
      case '/video/reference-create':
        setReferenceImagePageData({
          simple_mode: true,
          ...script,
        });
        break;
      case '/video/long/reference-create':
        setLongReferenceImagePageData({
          simple_mode: true,
          ...script,
        });
        break;
      case '/video/create':
        setFrameImagePageData({
          simple_mode: true,
          ...script,
        });
        break;
      default:
        notification.error({
          title: '错误',
          description: '数据错误，无法识别的脚本类型',
        });
        return;
    }

    navigate({ to });
  };

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          {
            key: '多图视频生成',
            label: '多图视频生成',
            onClick: () => {
              handleCreateVideo(script, '/video/reference-create');
            },
          },
          {
            key: '长视频生成',
            label: '长视频生成',
            onClick: () => {
              handleCreateVideo(script, '/video/long/reference-create');
            },
          },
          {
            key: '首尾帧视频',
            label: '首尾帧视频',
            onClick: () => {
              handleCreateVideo(script, '/video/create');
            },
          },
        ],
      }}
    >
      <Button
        loading={createScript.isPending}
        variant="filled"
        color="primary"
        icon={<VideoCameraAddOutlined />}
        {...props}
      >
        生成视频
      </Button>
    </Dropdown>
  );
};
